'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { LocateFixed, Loader2, X } from 'lucide-react';
import { setMyCity } from '@/lib/my-city';
import { placeHref } from '@/lib/place-url';
import { loadPlaceIndex, placeKey } from '@/lib/place-search';
import { nearestPlace, placesWithin, type Coords } from '@/lib/nearest-place';
import { geoAction, saveNearby } from '@/lib/nearby-cities';

// "Near me" (owner's approval, 2026-09-29; widened 2026-10-18): on a press - never before - the browser asks for the position; the nearest
// city of our list is worked out here, in the browser, from a list of coordinates fetched at that moment. The position is not sent anywhere
// and not kept. Only the city is (as "my city"), and, for this tab alone, the names of the cities within 100 km with their distance rounded
// to 5 km, so that the page of the nearest city can offer "also near you" (components/NearbyCities.tsx).
//
// Before the browser's own question comes a short explanation of what will happen and why, once. A position the browser has been told
// never to share is not asked for again: the person is told how to change that, or to choose a city.

type State = 'idle' | 'busy' | 'blocked' | 'unavailable' | 'far';

const EXPLAINED_KEY = 'rec:nearme:explained:v1';
const wasExplained = () => { try { return localStorage.getItem(EXPLAINED_KEY) === '1'; } catch { return false; } };
const markExplained = () => { try { localStorage.setItem(EXPLAINED_KEY, '1'); } catch { /* asked again next time */ } };

export default function NearMeButton({ lang, deal }: { lang: string; deal: 'sale' | 'rent' }) {
  const router = useRouter();
  const [state, setState] = useState<State>('idle');
  const [explaining, setExplaining] = useState(false);
  const holder = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  // The notes are drawn at a fixed place under the button: the row of cities this button sits in scrolls sideways and would clip them.
  const [anchor, setAnchor] = useState({ left: 8, top: 0 });
  const anchorUnder = (width: number) => {
    const r = button.current?.getBoundingClientRect();
    if (r) setAnchor({ left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), top: r.bottom + 8 });
  };
  const fr = lang === 'fr';

  const say = (s: State) => {
    if (s === 'blocked') anchorUnder(256);
    setState(s);
    window.setTimeout(() => setState('idle'), s === 'blocked' ? 9000 : 4000);
  };

  const locate = () => {
    setExplaining(false);
    setState('busy');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const [places, coords] = await Promise.all([loadPlaceIndex(), import('@/lib/places/canada-coords.json').then((m) => (m.default ?? m) as unknown as Coords)]);
          const position: [number, number] = [pos.coords.latitude, pos.coords.longitude];
          const city = nearestPlace(position, places, coords, placeKey);
          if (!city) return say('far');
          saveNearby(placesWithin(position, places, coords, placeKey, 100));
          setMyCity({ city: city.name, province: city.province });
          router.push(placeHref(lang, deal, { province: city.province, city: city.name }));
          setState('idle');
        } catch {
          say('unavailable');
        }
      },
      (err) => say(err.code === 1 ? 'blocked' : 'unavailable'),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 }
    );
  };

  const press = async () => {
    if (state === 'busy') return;
    if (!('geolocation' in navigator)) return say('unavailable');
    let permission: 'granted' | 'denied' | 'prompt' | 'unknown' = 'unknown';
    try {
      permission = (await navigator.permissions.query({ name: 'geolocation' })).state;
    } catch {
      // Safari and others cannot say: the browser's own question will.
    }
    const action = geoAction(permission, wasExplained());
    if (action === 'blocked') return say('blocked');
    if (action === 'explain') { anchorUnder(288); setExplaining(true); return; }
    locate();
  };

  // Escape and a press anywhere else close the explanation: it is a note, not a window.
  useEffect(() => {
    if (!explaining) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExplaining(false); };
    const onDown = (e: PointerEvent) => { if (holder.current && !holder.current.contains(e.target as Node)) setExplaining(false); };
    const away = () => setExplaining(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('resize', away);
    window.addEventListener('scroll', away, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('resize', away);
      window.removeEventListener('scroll', away, true);
    };
  }, [explaining]);

  const text: Record<State, string> = {
    idle: fr ? 'Près de moi' : 'Near me',
    busy: fr ? 'Localisation…' : 'Locating…',
    blocked: fr ? 'Position bloquée' : 'Location blocked',
    unavailable: fr ? 'Position indisponible' : 'Position unavailable',
    far: fr ? 'Aucune ville proche' : 'No city near you',
  };
  const note = {
    title: fr ? 'Trouver la ville la plus proche' : 'Find the city nearest to you',
    body: fr
      ? 'Votre navigateur va vous demander de partager votre position. Nous l’utilisons seulement dans votre navigateur, pour trouver la ville la plus proche de notre liste. Elle ne nous est pas envoyée et n’est pas conservée.'
      : 'Your browser will ask to share your position. We use it only in your browser, to find the closest city in our list. It is not sent to us and not kept.',
    go: fr ? 'Continuer' : 'Continue',
    cancel: fr ? 'Annuler' : 'Cancel',
    blocked: fr
      ? 'La position est bloquée pour ce site dans votre navigateur. Autorisez-la dans les réglages du site, ou choisissez une ville dans la liste.'
      : 'Location is blocked for this site in your browser. Allow it in the site settings, or choose a city from the list.',
    close: fr ? 'Fermer' : 'Close',
  };

  return (
    <span ref={holder} className="relative shrink-0 inline-flex">
      <button
        ref={button}
        type="button"
        onClick={press}
        aria-live="polite"
        aria-expanded={explaining}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-xl border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-xs text-neutral-200 hover:border-gold/50 hover:text-white transition-colors cursor-pointer"
      >
        {state === 'busy' ? <Loader2 aria-hidden="true" className="w-3.5 h-3.5 animate-spin" /> : <LocateFixed aria-hidden="true" className="w-3.5 h-3.5 text-champagne" />}
        {text[state]}
      </button>
      {state === 'blocked' && (
        <span role="status" style={{ position: 'fixed', left: anchor.left, top: anchor.top }} className="z-50 w-64 rounded-xl border border-neutral-700 bg-neutral-900 p-3 text-xs leading-relaxed text-neutral-200 shadow-2xl whitespace-normal">
          {note.blocked}
        </span>
      )}
      {explaining && (
        <span role="group" aria-label={note.title} style={{ position: 'fixed', left: anchor.left, top: anchor.top }} className="z-50 w-72 max-w-[calc(100vw-1rem)] rounded-xl border border-neutral-700 bg-neutral-900 p-4 text-xs leading-relaxed text-neutral-200 shadow-2xl whitespace-normal">
          <button type="button" onClick={() => setExplaining(false)} aria-label={note.close} className="absolute top-0.5 right-0.5 w-11 h-11 flex items-center justify-center rounded-full text-neutral-400 hover:text-white cursor-pointer">
            <X className="w-4 h-4" />
          </button>
          <strong className="block pr-8 text-sm text-white mb-1">{note.title}</strong>
          {note.body}
          <span className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setExplaining(false)} className="px-3 py-2.5 text-neutral-400 hover:text-white cursor-pointer">{note.cancel}</button>
            <button type="button" onClick={() => { markExplained(); locate(); }} className="px-4 py-2.5 rounded-xl bg-gold text-neutral-950 font-bold hover:bg-gold/85 transition-colors cursor-pointer">{note.go}</button>
          </span>
        </span>
      )}
    </span>
  );
}
